import {CategoriesApi} from './api/categoriesApi'
import {ChatApi} from './api/chatApi'
import {MakeRequest} from './api/request'
import {Secure} from './helpers/userSecure'
import {AdvertisesApi} from './api/advertisesApi'
import {FilterMapper} from './api/filterMapper'
import {ComplainApi} from './api/complainApi'
import {CreateAdvertiseApi} from './api/createAdvertiseApi'
import {GeoApi} from './api/geoApi'
import type {LocationModel} from './models'
import {GoogleApi} from './api/google.api'
import {StartApp} from './api/startApp'
import {SystemApi} from './api/systemApi'
import {FilterApi} from './api/filterApi'
import {UserApi} from './api/userApi'
import {OldRest} from './api/oldRest'
import {CommonUtils, CommonUtilsImplementation} from './utils/common'
import {DeeplinkUtils, DeeplinkUtilsImplementation} from './utils/deeplink'
import {AuthApi} from './api/authApi'
import {PaymentsApi} from './api/payments'

export * from './models'
export type {Translation} from './utils/common'
export * from './utils/langLocales'
export type {FetchAndSaveLocationResult} from './api/startApp'
export * from './utils/auth.utils'

export interface AppStorage {
  saveAppVersion(version: number): void
  setUserLocation(location: LocationModel | null): void
  setLocation(location: LocationModel | null): void
  saveAddressText(address: string): void
  readonly platform: 'ios' | 'android' | 'web'
  readonly language: string
  // must returns in that order filter location or userLocation or emptyLocation
  readonly location: LocationModel
  readonly searchAddress: string | null
  readonly userLocation: LocationModel | null
  readonly userHash: string | null
  // readonly phone: string | null
  readonly searchRadius: number
  readonly withPhoto: boolean
  // readonly authType: AuthType | null
  readonly fcmToken: string | null
  // readonly password: string | null
  readonly socialId: string | null
  // readonly promo: string | null
  // readonly email: string | null
  readonly token?: string
  readonly appVersion: number | null
  readonly searchBy?: 'coords' | 'id' | null
  readonly countryId?: number | null
  readonly regionId?: number | null
  readonly cityId?: number | null
  readonly authNewToken: string | undefined
  readonly authNewRefreshToken: string | undefined
  readonly saveNewTokens: (params: {
    id: string
    accessToken: string
    refreshToken: string
  }) => void
}

interface Options {
  storage: AppStorage
  isDev: boolean
  isLogRequest: boolean
  isLogResponse: boolean
  requestLocationPermissions?(): Promise<boolean>
  getIosLocation?(): Promise<LocationModel | null>
  endpoint: string
  newAuth: {
    onLogin: () => void
  }
  sendLog?: (message: string) => void
}

class RestApi {
  auth: AuthApi
  categories: CategoriesApi
  advertises: AdvertisesApi
  complain: ComplainApi
  createAdvertise: CreateAdvertiseApi
  chat: ChatApi
  geo: GeoApi
  google: GoogleApi
  startApp: StartApp
  systemApi: SystemApi
  filterApi: FilterApi
  filterMapper: FilterMapper
  user: UserApi
  oldRest: OldRest
  payments: PaymentsApi
  utils: CommonUtils
  deeplinkUtils: DeeplinkUtils

  readonly secure: Secure

  restFlatUrl = (code: string): string => {
    return `https://vooxee.com/img/flags/${code}.png`
  }

  constructor(options: Options) {
    this.utils = new CommonUtilsImplementation()
    this.deeplinkUtils = new DeeplinkUtilsImplementation()
    this.secure = new Secure(options.storage)
    const request = new MakeRequest(
      options.isLogRequest,
      options.endpoint,
      options.storage,
      options.newAuth,
      options.sendLog,
    )
    this.filterMapper = new FilterMapper(options.storage)
    this.categories = new CategoriesApi(request, options.storage, this.secure)
    this.chat = new ChatApi(request)
    this.advertises = new AdvertisesApi(
      request,
      options.storage,
      this.secure,
      this.filterMapper,
    )
    this.auth = new AuthApi(request, options.storage)

    this.complain = new ComplainApi(request, options.storage, this.secure)
    this.createAdvertise = new CreateAdvertiseApi(
      request,
      options.storage,
      this.secure,
    )

    this.geo = new GeoApi(request, options.storage)
    this.google = new GoogleApi(options.storage, options.storage.platform)
    this.startApp = new StartApp(
      request,
      options.storage,
      this.geo,
      this.google,
      options.requestLocationPermissions,
      options.getIosLocation,
    )

    this.systemApi = new SystemApi(
      request,
      options.storage,
      this.secure,
      this.deeplinkUtils,
    )
    this.filterApi = new FilterApi(
      options.isDev,
      request,
      options.storage,
      this.secure,
      this.filterMapper,
    )
    this.user = new UserApi(request, options.storage, this.secure)
    this.payments = new PaymentsApi(request)
    this.oldRest = new OldRest(
      options.isDev,
      options.endpoint,
      request,
      options.storage,
      this.secure,
      this.geo,
    )

    globalThis.isDev = options.isDev
  }
}

export default RestApi
